import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { fetchEventsForDevices } from '_services/coverage-service';
import { resolveWifiEventsToCoords, matchBle, nearestCentroid, buildCentroids, GPS_THRESHOLD_M } from './asset-tracking-utils';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';

// helpers 
function tsToDate(at) {
    return at?.toDate ? at.toDate() : new Date((at?.seconds ?? 0) * 1000);
}

function toDateInputValue(date) {
    return date.toISOString().slice(0, 10);
}

function formatDuration(ms) {
    if (ms < 0) return '—';
    const totalSec = Math.floor(ms / 1000);
    const days    = Math.floor(totalSec / 86400);
    const hours   = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    if (days > 0)  return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function formatDateTime(date) {
    if (!date) return '—';
    return date.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}


// Resolves a location_fixed event to a named location using centroids for GPS/WiFi.
function resolveLocation(evt, locations, centroids, wifiCoord) {
    if (!evt) return null;

    const lat = evt.decoded?.lat ?? evt.lat;
    const lng = evt.decoded?.lng ?? evt.lng;
    if (lat != null && lng != null) {
        const hit = nearestCentroid(lat, lng, centroids);
        const label = hit && hit.distance <= GPS_THRESHOLD_M
            ? `${hit.title} · ${hit.distance}m`
            : hit ? `Out of range · ${hit.title}` : `GPS ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        return { label, type: 'gps', lat, lng };
    }

    if (wifiCoord) {
        const hit = nearestCentroid(wifiCoord.lat, wifiCoord.lng, centroids);
        const label = hit && hit.distance <= GPS_THRESHOLD_M
            ? `${hit.title} · ${hit.distance}m`
            : hit ? `Out of range · ${hit.title}` : `WiFi ${wifiCoord.lat.toFixed(5)}, ${wifiCoord.lng.toFixed(5)}`;
        return { label, type: 'wifi', lat: wifiCoord.lat, lng: wifiCoord.lng };
    }

    const bleItems = evt.ble ?? evt.decoded?.ble ?? [];
    const title = matchBle(bleItems, locations);
    if (title) return { label: title, type: 'ble' };

    const nearest = [...bleItems].sort((a, b) => (b.rssi ?? -999) - (a.rssi ?? -999))[0];
    if (nearest?.mac) return { label: `BLE ${nearest.mac}`, type: 'ble', mac: nearest.mac };
    return null;
}

/**
 * Derive idle periods using only "Start of movement" events — the one reliable
 * signal across all device models.
 *
 * For each "Start of movement":
 *   - Find the LAST location_fixed between it and the NEXT "Start of movement".
 *   - Idle starts at that last fix (device was parked there).
 *   - Idle ends at the next "Start of movement" (or windowEnd if still idle).
 *   - Skip if no fix is found between the two starts (can't determine location).
 */
function deriveIdlePeriodsForDevice(events, windowStart, windowEnd, locations, centroids, wifiCoords) {
    const startIndices = [];
    for (let i = 0; i < events.length; i++) {
        const evt = events[i];
        if (evt.type === 'event' && evt.decoded?.eventType === 'Start of movement') {
            startIndices.push(i);
        }
    }

    if (!startIndices.length) return [];

    const periods = [];

    for (let s = 0; s < startIndices.length; s++) {
        const moveIdx     = startIndices[s];
        const nextMoveIdx = startIndices[s + 1] ?? null;
        const rangeEnd    = nextMoveIdx ?? events.length;

        const idleEnd    = nextMoveIdx != null ? tsToDate(events[nextMoveIdx].at) : windowEnd;
        const estimated  = nextMoveIdx == null;

        // Last location fix after this Start of movement and before the next
        let lastFix = null, lastFixIdx = null;
        for (let i = moveIdx + 1; i < rangeEnd; i++) {
            const e = events[i];
            if (e.type === 'location_fixed' || e.type === 'location_fixed_gps_limit') {
                lastFix = e; lastFixIdx = i;
            }
        }

        if (!lastFix) continue;

        const idleStart  = tsToDate(lastFix.at);
        const durationMs = idleEnd - idleStart;
        if (durationMs <= 0) continue;

        // Walk backwards from lastFix to find the most recent fix that resolves to a location.
        // The last fix may be an unresolved WiFi scan — earlier BLE/GPS fixes often resolve fine.
        let location = null;
        for (let i = lastFixIdx; i > moveIdx; i--) {
            const e = events[i];
            if (e.type !== 'location_fixed' && e.type !== 'location_fixed_gps_limit') continue;
            location = resolveLocation(e, locations, centroids, wifiCoords?.get(i));
            if (location) break;
        }

        periods.push({
            idleStart,
            idleEnd,
            durationMs,
            location,
            estimated,
            reason: estimated ? 'Still idle at end of window' : undefined,
        });
    }

    return periods;
}

function exportToCSV(rows, filename) {
    const headers = ['Device', 'Location', 'Idle Start', 'Idle End', 'Duration', 'Estimated'];
    const lines   = [headers.join(',')];
    rows.forEach(r => {
        lines.push([
            JSON.stringify(r.deviceName ?? ''),
            JSON.stringify(r.location?.label ?? 'Unknown'),
            JSON.stringify(formatDateTime(r.idleStart)),
            JSON.stringify(formatDateTime(r.idleEnd)),
            JSON.stringify(formatDuration(r.durationMs)),
            r.estimated ? 'Yes' : 'No',
        ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// presentational helpers 
const TYPE_BADGE = {
    ble:  { bg: '#e7f1ff', color: '#0d6efd', label: 'BLE'  },
    gps:  { bg: '#fff3cd', color: '#856404', label: 'GPS'  },
    wifi: { bg: '#ede9fe', color: '#5b21b6', label: 'WiFi' },
};

function LocationBadge({ location }) {
    if (!location) return <span style={{ color: '#adb5bd' }}>Unknown</span>;
    const badge = TYPE_BADGE[location.type];
    return (
        <>
            {location.label}
            {badge && (
                <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', background: badge.bg, color: badge.color, borderRadius: 3 }}>{badge.label}</span>
            )}
        </>
    );
}

function EstTag({ estimated, reason }) {
    if (!estimated) return null;
    return (
        <span title={reason} style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', background: '#fff3cd', color: '#856404', borderRadius: 3, cursor: 'help' }}>
            est.
        </span>
    );
}

// component 
const VIEW_MODES = [
    { key: 'all',        label: 'All'         },
    { key: 'byDevice',   label: 'By Device'   },
    { key: 'byLocation', label: 'By Location' },
];

export default function AssetTrackingIdleReport({ devices }) {

    const locations = useSelector(selectAirportLocations) ?? [];
    const centroids = useMemo(() => buildCentroids(locations), [locations]);

    const [startDate,      setStartDate]      = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return toDateInputValue(d); });
    const [endDate,        setEndDate]        = useState(() => toDateInputValue(new Date()));
    const [minIdleMinutes, setMinIdleMinutes] = useState(5);
    const [loading,        setLoading]        = useState(false);
    const [error,          setError]          = useState(null);
    const [rows,           setRows]           = useState([]);
    const [sortKey,        setSortKey]        = useState('durationMs');
    const [sortDir,        setSortDir]        = useState('desc');
    const [viewMode,       setViewMode]       = useState('all');
    const [expandedKeys,   setExpandedKeys]   = useState(new Set());

    // derived 
    const filteredRows = useMemo(() => {
        const minMs = minIdleMinutes * 60 * 1000;
        return rows.filter(r => r.durationMs >= minMs);
    }, [rows, minIdleMinutes]);

    const sortedRows = useMemo(() => {
        return [...filteredRows].sort((a, b) => {
            let av, bv;
            if      (sortKey === 'durationMs') { av = a.durationMs;                            bv = b.durationMs; }
            else if (sortKey === 'idleStart')  { av = a.idleStart?.getTime()  ?? 0;            bv = b.idleStart?.getTime()  ?? 0; }
            else if (sortKey === 'deviceName') { av = (a.deviceName  ?? '').toLowerCase();     bv = (b.deviceName  ?? '').toLowerCase(); }
            else if (sortKey === 'location')   { av = (a.location?.label ?? '').toLowerCase(); bv = (b.location?.label ?? '').toLowerCase(); }
            else { av = 0; bv = 0; }
            if (av < bv) return sortDir === 'asc' ? -1 :  1;
            if (av > bv) return sortDir === 'asc' ?  1 : -1;
            return 0;
        });
    }, [filteredRows, sortKey, sortDir]);

    const byDevice = useMemo(() => {
        const map = new Map();
        sortedRows.forEach(r => {
            if (!map.has(r.deviceEui)) {
                map.set(r.deviceEui, { deviceEui: r.deviceEui, deviceName: r.deviceName, periods: [], totalMs: 0 });
            }
            const e = map.get(r.deviceEui);
            e.periods.push(r);
            e.totalMs += r.durationMs;
        });
        return [...map.values()].sort((a, b) => b.totalMs - a.totalMs);
    }, [sortedRows]);

    const byLocation = useMemo(() => {
        const map = new Map();
        sortedRows.forEach(r => {
            const key = r.location?.label ?? 'Unknown';
            if (!map.has(key)) {
                map.set(key, { locationLabel: key, locationType: r.location?.type ?? null, periods: [], totalMs: 0, deviceSet: new Set() });
            }
            const e = map.get(key);
            e.periods.push(r);
            e.totalMs += r.durationMs;
            e.deviceSet.add(r.deviceEui);
        });
        return [...map.values()].sort((a, b) => b.totalMs - a.totalMs);
    }, [sortedRows]);

    const summary = useMemo(() => {
        if (!sortedRows.length) return null;
        const totalMs   = sortedRows.reduce((s, r) => s + r.durationMs, 0);
        const longestMs = Math.max(...sortedRows.map(r => r.durationMs));
        return {
            count:           sortedRows.length,
            totalIdle:       formatDuration(totalMs),
            longest:         formatDuration(longestMs),
            devicesAffected: new Set(sortedRows.map(r => r.deviceEui)).size,
        };
    }, [sortedRows]);

    // handlers
    async function loadReport() {
        setLoading(true);
        setError(null);
        setRows([]);
        setExpandedKeys(new Set());
        try {
            const start = new Date(startDate + 'T00:00:00');
            const end   = new Date(endDate   + 'T23:59:59');
            const eventsByDevice = await fetchEventsForDevices(devices, start, end);
            const iata = devices[0]?.meta?.tenantName;
            const allRows = [];
            for (const device of devices) {
                const events     = eventsByDevice[device.id] ?? [];
                const wifiCoords = await resolveWifiEventsToCoords(events, iata);
                const periods    = deriveIdlePeriodsForDevice(events, start, end, locations, centroids, wifiCoords);
                periods.forEach(p => allRows.push({
                    ...p,
                    deviceEui:  device.id,
                    deviceName: device.meta?.deviceName ?? device.id,
                    tenantName: device.meta?.tenantName ?? '',
                }));
            }
            setRows(allRows);
            if (allRows.length === 0) {
                setError('No idle periods found. Try widening the date range or reducing the minimum idle duration.');
            }
        } catch (e) {
            console.error('Idle report load error:', e);
            setError('Failed to load. Check console — first-time queries may need a Firestore index.');
        } finally {
            setLoading(false);
        }
    }

    function toggleSort(key) {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('desc'); }
    }
    const arrow = key => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    function toggleExpand(key) {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    }

    // shared table header cell 
    function TH({ children, onClick, right }) {
        return (
            <th onClick={onClick} style={{
                textAlign: right ? 'right' : 'left',
                padding: '8px 12px',
                borderBottom: '1px solid #dee2e6',
                cursor: onClick ? 'pointer' : 'default',
                userSelect: 'none',
                fontSize: 12, fontWeight: 600, color: '#495057',
                background: '#f8f9fa',
                position: 'sticky', top: 0, zIndex: 1,
                whiteSpace: 'nowrap',
            }}>{children}</th>
        );
    }

    // group summary row (By Device / By Location) 
    function GroupRow({ onClick, isExpanded, label, badge, col2, col3, col4 }) {
        return (
            <tr onClick={onClick} style={{ borderBottom: '1px solid #dee2e6', background: '#f0f6ff', cursor: 'pointer' }}>
                <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#1e3a5f' }}>
                    <span style={{ marginRight: 8, fontSize: 10, display: 'inline-block', width: 10 }}>
                        {isExpanded ? '▼' : '▶'}
                    </span>
                    {label}{badge}
                </td>
                <td style={{ padding: '8px 12px', fontSize: 12, color: '#495057' }}>{col2}</td>
                <td style={{ padding: '8px 12px', fontSize: 12, color: '#495057' }}>{col3}</td>
                <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', color: '#1e3a5f' }}>{col4}</td>
            </tr>
        );
    }

    // view renderers 
    function renderAllView() {
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <TH onClick={() => toggleSort('deviceName')}>Device{arrow('deviceName')}</TH>
                        <TH onClick={() => toggleSort('location')}>Location{arrow('location')}</TH>
                        <TH onClick={() => toggleSort('idleStart')}>Idle Start{arrow('idleStart')}</TH>
                        <TH>Idle End</TH>
                        <TH onClick={() => toggleSort('durationMs')} right>Duration{arrow('durationMs')}</TH>
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f3f5', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '7px 12px', fontSize: 12, fontWeight: 500 }}>
                                {r.deviceName}
                                {r.tenantName && <div style={{ fontSize: 10, color: '#adb5bd' }}>{r.tenantName}</div>}
                            </td>
                            <td style={{ padding: '7px 12px', fontSize: 12 }}>
                                <LocationBadge location={r.location} />
                            </td>
                            <td style={{ padding: '7px 12px', fontSize: 12, color: '#495057', whiteSpace: 'nowrap' }}>
                                {formatDateTime(r.idleStart)}
                            </td>
                            <td style={{ padding: '7px 12px', fontSize: 12, color: '#495057', whiteSpace: 'nowrap' }}>
                                {formatDateTime(r.idleEnd)}
                                <EstTag estimated={r.estimated} reason={r.reason} />
                            </td>
                            <td style={{ padding: '7px 12px', fontSize: 12, textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                                {formatDuration(r.durationMs)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    function renderByDeviceView() {
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <TH>Device</TH>
                        <TH>Idle Periods</TH>
                        <TH>Longest Idle</TH>
                        <TH right>Total Idle</TH>
                    </tr>
                </thead>
                <tbody>
                    {byDevice.map(entry => {
                        const isExpanded = expandedKeys.has(entry.deviceEui);
                        const longestMs  = Math.max(...entry.periods.map(p => p.durationMs));
                        return (
                            <React.Fragment key={entry.deviceEui}>
                                <GroupRow
                                    onClick={() => toggleExpand(entry.deviceEui)}
                                    isExpanded={isExpanded}
                                    label={entry.deviceName}
                                    col2={`${entry.periods.length} period${entry.periods.length !== 1 ? 's' : ''}`}
                                    col3={formatDuration(longestMs)}
                                    col4={formatDuration(entry.totalMs)}
                                />
                                {isExpanded && [...entry.periods]
                                    .sort((a, b) => b.durationMs - a.durationMs)
                                    .map((r, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f3f5', background: '#fff' }}>
                                            <td style={{ padding: '6px 12px 6px 32px', fontSize: 11, color: '#adb5bd' }}>↳</td>
                                            <td style={{ padding: '6px 12px', fontSize: 11, color: '#495057' }}>
                                                <LocationBadge location={r.location} />
                                            </td>
                                            <td style={{ padding: '6px 12px', fontSize: 11, color: '#6c757d', whiteSpace: 'nowrap' }}>
                                                {formatDateTime(r.idleStart)} → {formatDateTime(r.idleEnd)}
                                                <EstTag estimated={r.estimated} reason={r.reason} />
                                            </td>
                                            <td style={{ padding: '6px 12px', fontSize: 11, textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                                                {formatDuration(r.durationMs)}
                                            </td>
                                        </tr>
                                    ))
                                }
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    function renderByLocationView() {
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <TH>Location</TH>
                        <TH>Assets</TH>
                        <TH>Idle Periods</TH>
                        <TH right>Total Idle</TH>
                    </tr>
                </thead>
                <tbody>
                    {byLocation.map(entry => {
                        const isExpanded = expandedKeys.has(entry.locationLabel);
                        const typeBadge = entry.locationType ? TYPE_BADGE[entry.locationType] : null;
                        const badge = typeBadge ? (
                            <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 3, background: typeBadge.bg, color: typeBadge.color }}>
                                {typeBadge.label}
                            </span>
                        ) : null;
                        return (
                            <React.Fragment key={entry.locationLabel}>
                                <GroupRow
                                    onClick={() => toggleExpand(entry.locationLabel)}
                                    isExpanded={isExpanded}
                                    label={entry.locationLabel}
                                    badge={badge}
                                    col2={`${entry.deviceSet.size} asset${entry.deviceSet.size !== 1 ? 's' : ''}`}
                                    col3={`${entry.periods.length} period${entry.periods.length !== 1 ? 's' : ''}`}
                                    col4={formatDuration(entry.totalMs)}
                                />
                                {isExpanded && [...entry.periods]
                                    .sort((a, b) => b.durationMs - a.durationMs)
                                    .map((r, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f3f5', background: '#fff' }}>
                                            <td style={{ padding: '6px 12px 6px 32px', fontSize: 11, color: '#495057', fontWeight: 500 }}>
                                                ↳ {r.deviceName}
                                            </td>
                                            <td style={{ padding: '6px 12px', fontSize: 11, color: '#6c757d', whiteSpace: 'nowrap' }} colSpan={2}>
                                                {formatDateTime(r.idleStart)} → {formatDateTime(r.idleEnd)}
                                                <EstTag estimated={r.estimated} reason={r.reason} />
                                            </td>
                                            <td style={{ padding: '6px 12px', fontSize: 11, textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                                                {formatDuration(r.durationMs)}
                                            </td>
                                        </tr>
                                    ))
                                }
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    // layout 
    return (
        <div style={{ display: 'flex', height: '100%', gap: 12 }}>

            {/* ── Left panel ── */}
            <div style={{
                width: 250, flexShrink: 0,
                background: 'linear-gradient(175deg, #1e5fbb 0%, #307FE2 100%)',
                borderRadius: 12, overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(48,127,226,0.3)',
                display: 'flex', flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid rgba(255,255,255,0.12)', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>Idle Report</div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                    {/* Date range */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                            Date Range
                        </div>
                        <div>
                            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 3 }}>From</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.8rem', padding: '5px 8px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 3 }}>To</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.8rem', padding: '5px 8px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                            />
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }} />

                    {/* Run Report */}
                    <button
                        onClick={loadReport}
                        disabled={loading || !devices?.length}
                        style={{
                            padding: '8px 0', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: '0.83rem',
                            background: (loading || !devices?.length) ? 'rgba(255,255,255,0.12)' : '#fff',
                            color:      (loading || !devices?.length) ? 'rgba(255,255,255,0.35)' : '#307FE2',
                            cursor: (loading || !devices?.length) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                        }}
                    >
                        {loading ? 'Loading…' : 'Run Report'}
                    </button>

                    {/* Export CSV */}
                    <button
                        onClick={() => exportToCSV(sortedRows, `idle-report-${startDate}_to_${endDate}.csv`)}
                        disabled={sortedRows.length === 0}
                        style={{
                            padding: '7px 0', borderRadius: 7, fontWeight: 600, fontSize: '0.8rem',
                            background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.22)',
                            color: sortedRows.length === 0 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.85)',
                            cursor: sortedRows.length === 0 ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                        }}
                    >
                        Export CSV
                    </button>

                    {/* Summary stats */}
                    {summary && (
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                                Summary
                            </div>
                            {[
                                { label: 'Idle periods',      value: summary.count },
                                { label: 'Devices affected',  value: summary.devicesAffected },
                                { label: 'Total idle time',   value: summary.totalIdle },
                                { label: 'Longest',           value: summary.longest },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)' }}>
                                    <span>{label}</span><span style={{ fontWeight: 600 }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {error && (
                        <div style={{ fontSize: '0.75rem', color: '#fef3c7', background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: '7px 10px', lineHeight: 1.4, border: '1px solid rgba(255,211,77,0.3)' }}>
                            {error}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Right panel ── */}
            <div className='border' style={{
                flex: 1, borderRadius: 8, overflow: 'hidden',
                display: 'flex', flexDirection: 'column', background: '#fff',
            }}>
                {/* Header + view mode toggle */}
                <div style={{
                    padding: '10px 14px', borderBottom: '1px solid #dee2e6',
                    display: 'flex', alignItems: 'center', gap: 12,
                    flexShrink: 0,
                }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#1e3a5f', flexShrink: 0 }}>Idle Time Report</span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>

                        {/* Min idle duration slider */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: '#6c757d', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 90, textAlign: 'right' }}>
                                Min idle: <strong style={{ color: '#1e3a5f' }}>{formatDuration(minIdleMinutes * 60 * 1000)}</strong>
                            </span>
                            <input
                                type="range"
                                min={5}
                                max={10080}
                                step={15}
                                value={minIdleMinutes}
                                onChange={e => setMinIdleMinutes(parseInt(e.target.value, 10))}
                                style={{ width: 130, accentColor: '#307FE2', cursor: 'pointer' }}
                            />
                        </div>

                        <div style={{ width: 1, height: 20, background: '#dee2e6', flexShrink: 0 }} />

                        {/* View mode pill toggle */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 2,
                            background: '#f0f6ff', borderRadius: 8, padding: 3,
                        }}>
                            {VIEW_MODES.map(({ key, label }) => {
                                const active = viewMode === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => { setViewMode(key); setExpandedKeys(new Set()); }}
                                        style={{
                                            padding: '4px 14px', borderRadius: 6, border: 'none',
                                            fontSize: 11, fontWeight: active ? 700 : 500,
                                            background: active ? '#307FE2' : 'transparent',
                                            color:      active ? '#fff' : '#64748b',
                                            cursor: 'pointer',
                                            transition: 'all 0.12s',
                                            boxShadow: active ? '0 1px 4px rgba(48,127,226,0.3)' : 'none',
                                        }}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Table area */}
                <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
                    {sortedRows.length === 0 && !loading && (
                        <div style={{
                            position: 'absolute', inset: 0, zIndex: 10,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(240,246,255,0.92)', pointerEvents: 'none', gap: 10,
                        }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: '50%', background: '#E6F0FB',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#307FE2" strokeWidth="1.8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e3a5f' }}>No data yet</div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Set a date range and run the report</div>
                        </div>
                    )}

                    {sortedRows.length > 0 && viewMode === 'all'        && renderAllView()}
                    {sortedRows.length > 0 && viewMode === 'byDevice'   && renderByDeviceView()}
                    {sortedRows.length > 0 && viewMode === 'byLocation' && renderByLocationView()}
                </div>
            </div>

        </div>
    );
}
